import React, { useEffect, useMemo, useState } from 'react';
import { Text, textVariantsEnum, fontWeightEnum } from '@components/Text';
import { TabBar, tabBarVariants, tabBarSizes } from '@components/TabBar';
import { Table } from '@components/Table';
import { Pagination } from '@components/Pagination';
import { InfoTooltip } from '@components/InfoTooltip';

import lateEarlyAlertsService from '@/services/lateEarlyAlerts';
import { getAttendanceExceptionsColumns } from '../LateEarlyLoginAlerts.config';
// .card is shared from the parent page's module — see LateEarlyLoginAlerts.module.scss.
import pageStyles from '../LateEarlyLoginAlerts.module.scss';
import styles from './ExceptionsTable.module.scss';

const PAGE_SIZE = 10;
// Bounded fetch cap, not "every employee" — exceptions per day are
// naturally bounded by headcount actually late/early, never the whole
// company. Client-paginated from here rather than server cursor pages,
// since this list is small enough that one fetch comfortably covers it.
const FETCH_CAP = 200;

export const ExceptionsTable = ({
  teamId,
  dateId,
  timeZone,
  lateCount,
  earlyCount,
  memberDirectory,
}) => {
  const [activeTab, setActiveTab] = useState('all');
  const [page, setPage] = useState(1);
  const [records, setRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setPage(1);
  }, [activeTab, teamId, dateId]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!teamId || !dateId) return;
      setIsLoading(true);

      const res = await lateEarlyAlertsService.getAttendanceExceptions(
        teamId,
        dateId,
        {
          type: activeTab,
          pageSize: FETCH_CAP,
          timeZone,
        }
      );

      if (cancelled) return;
      setRecords(res.data);
      setIsLoading(false);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [teamId, dateId, activeTab, timeZone]);

  const totalPages = Math.max(1, Math.ceil(records.length / PAGE_SIZE));
  const pagedRecords = records.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const columns = useMemo(
    () => getAttendanceExceptionsColumns({ memberDirectory, timeZone }),
    [memberDirectory, timeZone]
  );

  const tabs = [
    { label: 'All', value: 'all', count: lateCount + earlyCount },
    { label: 'Late Arrivals', value: 'late', count: lateCount },
    { label: 'Early Arrivals', value: 'early', count: earlyCount },
  ];

  return (
    <div className={pageStyles.card}>
      <div className={styles.header}>
        <div className={styles.titleGroup}>
          <Text
            variant={textVariantsEnum.Text_sm}
            fontWeight={fontWeightEnum.Semibold}
            color="gray-900"
          >
            Attendance Exceptions
          </Text>
          <InfoTooltip
            title="Attendance Exceptions"
            description="Employees who checked in outside their team's scheduled start time on this day."
          />
        </div>
      </div>

      <TabBar
        tabs={tabs}
        activeTab={activeTab}
        onTabClick={setActiveTab}
        variant={tabBarVariants.underline}
        size={tabBarSizes.sm}
      />

      {isLoading ? (
        <div className={styles.loadingState}>
          <Text variant={textVariantsEnum.Text_xs} color="gray-500">
            Loading exceptions…
          </Text>
        </div>
      ) : records.length === 0 ? (
        <div className={pageStyles.emptyState}>
          No late or early check-ins for this day.
        </div>
      ) : (
        <>
          <Table data={pagedRecords} columns={columns} />
          <div className={styles.footer}>
            <Text variant={textVariantsEnum.Text_xs} color="gray-500">
              Showing {(page - 1) * PAGE_SIZE + 1}–
              {Math.min(page * PAGE_SIZE, records.length)} of {records.length}
            </Text>
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
            />
          </div>
        </>
      )}
    </div>
  );
};

ExceptionsTable.displayName = 'ExceptionsTable';
